import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import styles from './GlobalTabs.module.css';

type TabItem = {
  label: string;
  content: React.ReactNode;
  queryValue?: string; // Optional query param value for this tab
};

type GlobalTabsProps = {
  tabs: TabItem[];
  initialIndex?: number;
  onChange?: (index: number) => void;
  queryParam?: string; // Query param name to sync with (e.g., 'tab')
  defaultTabValue?: string; // Default tab value when no param is present
};

export default function GlobalTabs({ 
  tabs, 
  initialIndex = 0, 
  onChange,
  queryParam,
  defaultTabValue
}: GlobalTabsProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Determine initial selected index from URL or prop
  const getInitialIndex = () => {
    if (queryParam) {
      const paramValue = searchParams.get(queryParam);
      if (paramValue) {
        // Find tab index by queryValue
        const tabIndex = tabs.findIndex(tab => tab.queryValue === paramValue);
        if (tabIndex !== -1) return tabIndex;
      }
      // If param exists but no match, check if defaultTabValue matches
      if (defaultTabValue && paramValue === defaultTabValue) {
        return 0;
      }
    }
    return initialIndex;
  };

  const [selected, setSelected] = useState(getInitialIndex);
  const indicatorRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const tabListRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef(tabs);
  
  // Keep tabs ref up to date
  useEffect(() => {
    tabsRef.current = tabs;
  }, [tabs]);

  // Sync selected state with URL query param changes ONLY (not when tabs array changes)
  useEffect(() => {
    if (queryParam) {
      const paramValue = searchParams.get(queryParam);
      if (paramValue) {
        const tabIndex = tabsRef.current.findIndex(tab => tab.queryValue === paramValue);
        if (tabIndex !== -1) {
          setSelected(prev => prev !== tabIndex ? tabIndex : prev);
        } else if (defaultTabValue && paramValue === defaultTabValue) {
          // Default tab value matches
          setSelected(prev => prev !== 0 ? 0 : prev);
        } else {
          // Tab param exists but doesn't match any tab - reset to default
          // (The parent component should handle removing the param when switching menu items)
          setSelected(initialIndex);
        }
      } else {
        // No tab param - ensure we're on the default tab
        setSelected(prev => prev !== initialIndex ? initialIndex : prev);
      }
    }
    // Don't sync with initialIndex on re-renders - only use it on initial mount via useState
  }, [searchParams, queryParam, defaultTabValue, initialIndex]);

  useEffect(() => {
    const currentTab = tabRefs.current[selected];
    const indicator = indicatorRef.current;
    const tabList = tabListRef.current;

    if (currentTab && indicator) {
      const { offsetLeft, offsetWidth } = currentTab;
      indicator.style.left = `${offsetLeft}px`;
      indicator.style.width = `${offsetWidth}px`;
    }

    // Smooth scroll to ensure selected tab is visible
    if (currentTab && tabList) {
      const tabListRect = tabList.getBoundingClientRect();
      const tabRect = currentTab.getBoundingClientRect();
      
      // Check if tab is outside the visible area
      if (tabRect.left < tabListRect.left) {
        // Tab is to the left of visible area
        tabList.scrollTo({
          left: tabList.scrollLeft + (tabRect.left - tabListRect.left),
          behavior: 'smooth'
        });
      } else if (tabRect.right > tabListRect.right) {
        // Tab is to the right of visible area
        tabList.scrollTo({
          left: tabList.scrollLeft + (tabRect.right - tabListRect.right),
          behavior: 'smooth'
        });
      }
    }
  }, [selected, tabs]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.tabList} ref={tabListRef}>
        {tabs.map((tab, index) => (
          <button
            type="button"
            key={index}
            ref={el => { tabRefs.current[index] = el }}
            className={`${styles.tab} ${selected === index ? styles.active : ''}`}
            onClick={() => {
              setSelected(index);
              onChange?.(index);
              
              // Update URL query param if queryParam is specified
              if (queryParam) {
                const newSearchParams = new URLSearchParams(searchParams);
                const tab = tabs[index];
                const tabValue = tab.queryValue;
                
                // Check if this is the default tab (first tab with matching defaultTabValue)
                const isDefaultTab = index === 0 && (
                  (defaultTabValue && tabValue === defaultTabValue) ||
                  (!defaultTabValue && !tabValue)
                );
                
                if (isDefaultTab) {
                  // For default tab, remove param to keep URL clean
                  newSearchParams.delete(queryParam);
                } else if (tabValue) {
                  // Set the query param value
                  newSearchParams.set(queryParam, tabValue);
                }
                
                setSearchParams(newSearchParams, { replace: true });
              }
            }}
          >
            {tab.label}
          </button>
        ))}
        <div className={styles.indicator} ref={indicatorRef} />
      </div>
      <div className={styles.tabContent} key={`tab-content-${selected}`}>
        {tabs[selected]?.content}
      </div>
    </div>
  );
}